import { useMemo, type ReactNode } from 'react';
import { Smile } from 'lucide-react';
import { cn } from '@cyphex/ui';
import { useCustomEmoji } from '../hooks/useCustomEmoji';
import { CustomEmojiButton } from './CustomEmojiButton';

export interface CustomEmojiSectionProps {
  /** Same needle the unicode picker is filtering by. */
  query: string;
  onSelect: (token: string) => void;
  /** Rendered under the grid — usually a "Manage emoji" affordance. */
  footer?: ReactNode;
  className?: string;
}

/**
 * The picker's tenant section.
 *
 * Rendered FIRST, above the unicode groups: a workspace's own emoji are the
 * ones people are looking for, and burying them under 1,800 unicode faces is
 * why custom emoji features go unused.
 */
export function CustomEmojiSection({
  query,
  onSelect,
  footer,
  className,
}: CustomEmojiSectionProps) {
  const { data } = useCustomEmoji();
  const needle = query.trim().toLowerCase();

  const matches = useMemo(() => {
    const all = data?.emoji ?? [];
    if (needle === '') return all.slice(0, 48);
    return all.filter((emoji) => emoji.name.includes(needle)).slice(0, 48);
  }, [data?.emoji, needle]);

  if (matches.length === 0 && footer === undefined) return null;

  return (
    <section className={cn('mb-2', className)}>
      <h3 className="mb-1 flex items-center gap-1 px-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Smile size={12} aria-hidden />
        Workspace
      </h3>
      {matches.length > 0 ? (
        <div className="grid grid-cols-8 gap-0.5">
          {matches.map((emoji) => (
            <CustomEmojiButton key={emoji.id} emoji={emoji} onSelect={onSelect} />
          ))}
        </div>
      ) : (
        <p className="px-1 py-1 text-2xs text-muted-foreground">
          {needle === '' ? 'No workspace emoji yet.' : 'No workspace emoji match.'}
        </p>
      )}
      {footer}
    </section>
  );
}
